import { t } from 'i18next'
import { useFormContext } from 'react-hook-form'

import { Button, InputField } from '@/components/ui'
import type {
  CompradorPessoaFisicaStepSchemaInput,
  CompradorPessoaFisicaStepSchemaOutput,
  CompradorVinculoStepSchemaInput,
  CompradorVinculoStepSchemaOutput,
} from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pf.schema'
import type {
  CompradorPessoaJuridicaSchemaInput,
  CompradorPessoaJuridicaSchemaOutput,
} from '@/features/proposta/brasil/domain/schemas/compradores/comprador-pj.schema'
import {
  StepPropostaContent,
  StepPropostaFooter,
  StepPropostaHeaderContent,
  StepPropostaHeaderTitle,
  StepPropostaRoot,
} from '@/features/proposta/shared/components/layouts/step-proposta'
import { useGuardedHandler } from '@/hooks/use-guarded-handlers'
import { PhoneNumberInputField } from '@/shared/phone-number'
import { checkOnlineGuard } from '@/utils'
import { configTelefoneBrasil } from '@/utils/telefone.utils'

type TContatosCompradorSchemaOutput =
  | CompradorPessoaFisicaStepSchemaOutput
  | CompradorPessoaJuridicaSchemaOutput
  | CompradorVinculoStepSchemaOutput

type TContatosCompradorSchemaInput =
  | CompradorPessoaFisicaStepSchemaInput
  | CompradorPessoaJuridicaSchemaInput
  | CompradorVinculoStepSchemaInput

type TContatosComprador = {
  fieldName: 'contato' | 'conjuge.contato' | 'maePai.contato' | 'responsavel.contato'
  isLast: boolean
  onBack: () => void
  onNext: () => void
  titulo: string
  onFinish: (value: TContatosCompradorSchemaOutput) => Promise<void>
}

export const ContatosComprador = ({ onBack, onNext, isLast, onFinish, titulo, fieldName }: TContatosComprador) => {
  const {
    control,
    trigger,
    setValue,
    handleSubmit,
    formState: { isSubmitting },
  } = useFormContext<TContatosCompradorSchemaInput, unknown, TContatosCompradorSchemaOutput>()
  const prefix = 'proposta:cadastro_compradores.steps.contatos'

  const handleConcluirCadastro = useGuardedHandler(
    [checkOnlineGuard()],
    async (formOutput: TContatosCompradorSchemaOutput) => await onFinish(formOutput),
  )

  const handleOnNext = async () => {
    if (isLast) {
      handleSubmit(handleConcluirCadastro)()
      return
    }
    // trigger(fieldName) não re-renderiza o useController de campos com 3 níveis; validar por subcaminhos diretos
    const isValid = await trigger([`${fieldName}.telefone`, `${fieldName}.email`])
    if (!isValid) return
    onNext()
  }

  return (
    <StepPropostaRoot>
      <StepPropostaHeaderContent>
        <StepPropostaHeaderTitle>{titulo}</StepPropostaHeaderTitle>
      </StepPropostaHeaderContent>
      <StepPropostaContent hasBorder>
        <div className="w-full">
          <PhoneNumberInputField
            config={configTelefoneBrasil}
            className="w-full sm:w-90"
            control={control}
            setValue={setValue}
            nationalityName={`${fieldName}.telefone.origem`}
            phoneName={`${fieldName}.telefone.numero`}
            label={t(`${prefix}.label.telefone`)}
          />
          <InputField
            className="w-full sm:w-90"
            control={control}
            placeholder={t(`${prefix}.placeholder.email`)}
            label={t(`${prefix}.label.email`)}
            name={`${fieldName}.email`}
            maxLength={100}
          />
        </div>
      </StepPropostaContent>
      <StepPropostaFooter>
        <Button variant="outline" onClick={onBack} className="font-nunito h-12 w-full md:w-auto">
          {t('proposta:cadastro_compradores.steps.voltar')}
        </Button>
        <Button
          className="font-nunito h-12 w-full md:w-27"
          isLoading={isSubmitting}
          showLoadingText={false}
          variant="default"
          onClick={handleOnNext}
          disabled={isSubmitting}
        >
          {t(isLast ? 'proposta:cadastro_compradores.steps.concluir' : 'proposta:cadastro_compradores.steps.proximo')}
        </Button>
      </StepPropostaFooter>
    </StepPropostaRoot>
  )
}
